/**
 * AppraiserPicker · Appraisal Booked · Action 60
 * ───────────────────────────────────────────────
 * Searchable dropdown of users from GET /users/list-lite. Operator picks
 * an existing user OR ticks "+ Enter a new appraiser manually" to type in
 * a name + email that isn't in the users table yet.
 *
 * Writes on Next:
 *   appraisal.appraiser_id    = <user._id> | null (when manual)
 *   appraisal.appraiser_name  = <name>
 *   appraisal.appraiser_email = <email>
 * Runner captured_data patch (via write_to):
 *   [write_to]: { appraiser_id, name, email }
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Loader2, Pencil, Check, X, Search, UserPlus } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function AppraiserPicker({
  action, capturedData, value, onChange, onNext, onBack, canBack,
  statusKey, taskId, onTemplateChanged,
}) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const allowNew = params.allow_new !== false;

  const seed = (typeof value === 'object' && value) ? value : {};
  const [picked, setPicked] = useState(seed.appraiser_id ? {
    _id: seed.appraiser_id, name: seed.name || '', email: seed.email || '',
  } : null);
  const [manualMode, setManualMode] = useState(!!seed.name && !seed.appraiser_id);
  const [manual, setManual] = useState({ name: seed.name || '', email: seed.email || '' });

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const searchTimer = useRef(null);

  const [promptDraft, setPromptDraft] = useState(params.prompt || '');
  const [editingPrompt, setEditingPrompt] = useState(false);

  // Reset on action switch
  useEffect(() => {
    const s = (typeof value === 'object' && value) ? value : {};
    setPicked(s.appraiser_id ? { _id: s.appraiser_id, name: s.name || '', email: s.email || '' } : null);
    setManualMode(!!s.name && !s.appraiser_id);
    setManual({ name: s.name || '', email: s.email || '' });
    setQuery(''); setResults([]);
  }, [action.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPromptDraft(params.prompt || ''); }, [params.prompt, action.id]);

  // iter303ai-fix8 · Auto-select when there's only one appraiser on file.
  // Runs once per action switch when the operator hasn't already picked
  // someone (from a previous visit) and manual-mode isn't active. Tries
  // `role=appraiser` first (proper role filter), falls back to unfiltered
  // list if no roles are tagged. Attaches a small "auto-selected" flag so
  // the UI can hint at the pre-fill.
  const [autoSelected, setAutoSelected] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = (typeof value === 'object' && value) ? value : {};
      if (s.appraiser_id || (s.name && !s.appraiser_id)) return; // already picked/manual
      try {
        // Prefer role-filtered
        let { data } = await api.get('/users/list-lite', { params: { role: 'appraiser', limit: 5 } });
        if (!Array.isArray(data) || data.length === 0) {
          // Fallback: no role-tagged users → try unfiltered list.
          const res2 = await api.get('/users/list-lite', { params: { limit: 5 } });
          data = Array.isArray(res2.data) ? res2.data : [];
        }
        if (cancelled) return;
        if (data.length === 1) {
          const u = data[0];
          setPicked({ _id: u._id, name: u.name || u.email || 'Unnamed', email: u.email || '' });
          setAutoSelected(true);
          onChange({ appraiser_id: u._id, name: u.name || u.email || 'Unnamed', email: u.email || '' });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [action.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live search
  useEffect(() => {
    if (manualMode) { setResults([]); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true); setErr('');
      try {
        const q = query.trim();
        const { data } = await api.get('/users/list-lite', { params: { search: q, limit: 12 } });
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || 'Search failed');
      } finally { setSearching(false); }
    }, 220);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [query, manualMode]);

  const canEdit = !!(statusKey && taskId && action?.id);
  const savePrompt = async () => {
    setSaving(true); setErr('');
    try {
      await api.patch(
        `/status-workflows/workflow/${statusKey}/tasks/${taskId}/actions/${action.id}`,
        { params: { prompt: promptDraft.trim() } },
      );
      if (onTemplateChanged) await onTemplateChanged();
      setEditingPrompt(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const pickUser = (u) => {
    setPicked({ _id: u._id, name: u.name || u.email || 'Unnamed', email: u.email || '' });
    setQuery(''); setResults([]);
    setManualMode(false);
    onChange({ appraiser_id: u._id, name: u.name || u.email || 'Unnamed', email: u.email || '' });
  };
  const clearPick = () => {
    setPicked(null);
    onChange({ appraiser_id: null, name: manualMode ? manual.name : '', email: manualMode ? manual.email : '' });
  };
  const toggleManual = () => {
    setManualMode(m => {
      const next = !m;
      if (next) {
        setPicked(null);
        onChange({ appraiser_id: null, name: manual.name, email: manual.email });
      } else {
        setManual({ name: '', email: '' });
        onChange({ appraiser_id: null, name: '', email: '' });
      }
      return next;
    });
  };
  const setManualField = (k, v) => {
    const nextM = { ...manual, [k]: v };
    setManual(nextM);
    onChange({ appraiser_id: null, name: nextM.name, email: nextM.email });
  };

  const chosenName = picked ? picked.name : manualMode ? manual.name.trim() : '';
  const chosenEmail = picked ? picked.email : manualMode ? manual.email.trim() : '';
  const chosenId = picked ? picked._id : null;
  const canProceed = !!chosenName;

  const commit = async () => {
    if (!canProceed) return;
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          appraiser_id: chosenId,
          appraiser_name: chosenName,
          appraiser_email: chosenEmail,
        });
      }
      const out = { appraiser_id: chosenId, name: chosenName, email: chosenEmail };
      onChange(out);
      onNext(out);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="wf-appraiser-picker">
      <div className="bg-gradient-to-br from-indigo-50 to-white border-l-4 border-indigo-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-semibold mb-2 flex items-center gap-2">
          <span>👤 Assign Appraiser</span>
          {canEdit && !editingPrompt && (
            <button onClick={() => setEditingPrompt(true)} data-testid="wf-appraiser-prompt-edit"
              className="text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
              <Pencil size={10} /> edit
            </button>
          )}
        </div>
        {editingPrompt ? (
          <div>
            <textarea rows={3} value={promptDraft} onChange={e => setPromptDraft(e.target.value)}
              data-testid="wf-appraiser-prompt-input"
              className="w-full text-base text-slate-800 leading-relaxed border border-indigo-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={savePrompt} disabled={saving || !promptDraft.trim()}
                className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
              <button onClick={() => { setEditingPrompt(false); setPromptDraft(params.prompt || ''); }}
                className="px-3 py-1 text-slate-500 text-xs hover:text-slate-700 flex items-center gap-1">
                <X size={12} /> Cancel
              </button>
              {err && <span className="text-xs text-rose-600">{err}</span>}
            </div>
          </div>
        ) : (
          <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-appraiser-prompt">
            {params.prompt || 'Which appraiser is going out?'}
          </div>
        )}
      </div>

      {!manualMode && (
        <div className="mb-4">
          <label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1">
            <Search size={12} /> Search users (name or email)
          </label>
          <div className="relative">
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to filter…"
              data-testid="wf-appraiser-search-input"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            {searching && <Loader2 size={14} className="animate-spin absolute right-3 top-2.5 text-slate-400" />}
          </div>
          {results.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded shadow-sm max-h-64 overflow-auto bg-white" data-testid="wf-appraiser-results">
              {results.map(u => (
                <button key={u._id} type="button" onClick={() => pickUser(u)}
                  data-testid={`wf-appraiser-result-${u._id}`}
                  className="w-full text-left px-3 py-2 border-b last:border-b-0 border-slate-100 hover:bg-indigo-50">
                  <div className="font-semibold text-sm text-slate-800">{u.name || '(unnamed)'}</div>
                  <div className="text-xs text-slate-500">{u.email} {u.role && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 text-[10px] uppercase">{u.role}</span>}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {picked && !manualMode && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded flex items-center gap-2" data-testid="wf-appraiser-picked">
          <div className="flex-1">
            <div className="text-xs uppercase text-indigo-500 font-semibold flex items-center gap-2">
              Selected appraiser
              {autoSelected && (
                <span data-testid="wf-appraiser-auto-selected"
                  className="normal-case font-normal text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
                  auto-selected (only appraiser on file)
                </span>
              )}
            </div>
            <div className="font-semibold text-slate-800">{picked.name}</div>
            <div className="text-xs text-slate-500">{picked.email}</div>
          </div>
          <button type="button" onClick={clearPick} data-testid="wf-appraiser-clear"
            className="text-xs text-indigo-700 hover:text-indigo-900 flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {allowNew && (
        <div className="mb-4">
          <button type="button" onClick={toggleManual}
            data-testid="wf-appraiser-toggle-manual"
            className={`px-3 py-1.5 text-xs border-2 rounded flex items-center gap-1 ${manualMode
              ? 'border-indigo-500 bg-indigo-100 text-indigo-800'
              : 'border-dashed border-slate-300 text-slate-500 hover:border-indigo-300 hover:text-indigo-700'}`}>
            <UserPlus size={12} /> {manualMode ? 'Switch back to user search' : 'Enter a new appraiser manually'}
          </button>
        </div>
      )}

      {manualMode && (
        <div className="mb-4 p-3 border-2 border-indigo-200 bg-indigo-50/50 rounded space-y-2" data-testid="wf-appraiser-manual-block">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Appraiser name *</label>
            <input type="text" value={manual.name} onChange={e => setManualField('name', e.target.value)}
              data-testid="wf-appraiser-manual-name"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Email (optional)</label>
            <input type="email" value={manual.email} onChange={e => setManualField('email', e.target.value)}
              data-testid="wf-appraiser-manual-email"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div className="text-[11px] text-slate-500">
            Saved to the appraisal directly — the appraiser is <b>not</b> added to the users table.
          </div>
        </div>
      )}

      {params.write_to && (
        <div className="text-xs text-slate-400 mb-4">
          <span className="font-semibold text-slate-500">Writes to:</span> <code>{params.write_to}</code> · updates <code>appraisal.appraiser_id/name/email</code>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={commit} disabled={!canProceed || saving}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Next →
        </button>
      </div>
    </div>
  );
}
